<template>
  <card card-body-classes="table-full-width" no-footer-line>
    <h4 slot="header" class="card-title">Search Bookings</h4>
    <div class="card-body booking-search">
      <form class="booking-search__bar" @submit.prevent="runSearchNow">
        <div class="booking-search__field">
          <fg-input
            id="bookings_searchQuery"
            class="booking-search__input"
            :class="{ 'has-clear': !!searchQuery }"
            v-model="searchQuery"
            placeholder="Name, phone, email, staff or service…"
            addon-left-icon="now-ui-icons ui-1_zoom-bold"
            @keyup.native.esc="clearSearch"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="booking-search__clear"
            aria-label="Clear search"
            @click="clearSearch"
          >
            <i class="now-ui-icons ui-1_simple-remove" aria-hidden="true"></i>
          </button>
        </div>
      </form>

      <p class="booking-search__hint">
        Partial names, last names, small typos, and Irish phone formats all
        work. You do not need an exact match.
      </p>

      <div class="booking-search__meta">
        <div class="booking-search__status">
          <span v-if="searching">Looking through bookings…</span>
          <span v-else-if="tooShort">Type at least 2 characters.</span>
          <span v-else-if="searched && !filteredBookings.length">
            No bookings matched “{{ displayQuery }}”.
          </span>
          <span v-else-if="filteredBookings.length">
            {{ filteredBookings.length }}
            {{ filteredBookings.length === 1 ? "match" : "matches" }}
            · last {{ lookbackMonths }} months
          </span>
        </div>
        <div class="booking-search__filters">
          <button
            v-for="option in rangeOptions"
            :key="option.value"
            type="button"
            class="booking-search__chip"
            :class="{ 'is-on': rangeFilter === option.value }"
            @click="rangeFilter = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="d-flex align-items-center mb-2">
        <b-form-select
          v-model="pagination.perPage"
          aria-controls="bookings-search-table"
          :options="pagination.perPageOptions"
          class="booking-search__pagesize"
        />
      </div>

      <b-table
        id="bookings-search-table"
        :items="filteredBookings"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-compare="sortCompare"
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
        :busy="searching"
        :tbody-tr-class="rowClass"
        striped
        hover
        stacked="md"
        show-empty
        @row-clicked="openOnCalendar"
      >
        <template v-slot:cell(name)="data">
          <div class="booking-search__name">
            <strong v-html="highlight(data.item.name)"></strong>
            <div class="booking-search__badges">
              <b-badge
                v-if="data.item.bookingStatus === 'CANCELLED'"
                variant="danger"
              >
                Cancelled
              </b-badge>
              <b-badge
                v-else-if="data.item.bookingStatus === 'UNCONFIRMED'"
                variant="warning"
              >
                Unconfirmed
              </b-badge>
              <b-badge
                v-else-if="data.item.bookingStatus === 'CONFIRMED'"
                variant="info"
              >
                Confirmed
              </b-badge>
              <b-badge
                v-if="data.item.paymentStatus === 'NOT_PAID' && data.item.bookingStatus !== 'CANCELLED'"
                variant="danger"
              >
                Unpaid
              </b-badge>
            </div>
            <div
              v-if="data.item.serviceName || data.item.employeeName"
              class="booking-search__sub"
            >
              {{ serviceLine(data.item) }}
            </div>
            <div v-if="matchLine(data.item)" class="booking-search__match">
              {{ matchLine(data.item) }}
            </div>
          </div>
        </template>
        <template v-slot:cell(number)="data">
          <span v-html="highlight(data.item.number)"></span>
        </template>
        <template v-slot:cell(email)="data">
          <span v-html="highlight(data.item.email)"></span>
        </template>
        <template v-slot:cell(date)="data">
          {{ data.item.date }}
        </template>
        <template v-slot:empty>
          <div class="text-center text-muted py-4">
            {{ emptyCopy }}
          </div>
        </template>
      </b-table>

      <p v-if="filteredBookings.length" class="booking-search__hint mb-2">
        Click a result to open that day on the calendar.
      </p>
      <b-pagination
        v-if="filteredBookings.length > pagination.perPage"
        v-model="pagination.currentPage"
        :total-rows="filteredBookings.length"
        :per-page="pagination.perPage"
        align="right"
        aria-controls="bookings-search-table"
        first-number
        last-number
      />
    </div>
  </card>
</template>

<script>
import bookings from "@/store/bookings";
import { mapActions, mapGetters } from "vuex";
import {
  BOOKING_SEARCH_LOOKBACK_MONTHS,
  BOOKING_SEARCH_MIN_CHARS,
  errorMessage,
  formatMatchedIn,
  highlightSearchText
} from "@/helpers/bookingSearch";

export default {
  data() {
    return {
      lookbackMonths: BOOKING_SEARCH_LOOKBACK_MONTHS,
      fields: [
        {
          key: "name",
          label: "Customer",
          sortable: true
        },
        {
          key: "number",
          label: "Phone",
          sortable: false
        },
        {
          key: "email",
          label: "Email",
          sortable: false
        },
        {
          key: "date",
          label: "Date",
          sortable: true
        },
        {
          key: "time",
          label: "Time",
          sortable: true
        }
      ],
      searchQuery: "",
      sortBy: "",
      bookings: [],
      sortDesc: false,
      searching: false,
      searched: false,
      rangeFilter: "all",
      rangeOptions: [
        { value: "all", label: "All" },
        { value: "upcoming", label: "Upcoming" },
        { value: "past", label: "Past" }
      ],
      searchTimer: null,
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [10, 25, 50, 100],
        total: 0
      }
    };
  },
  computed: {
    ...mapGetters({
      shopId: "shops/shopId",
      employeeById: "employees/employeeById",
      serviceById: "services/serviceById"
    }),
    displayQuery() {
      return (this.searchQuery || "").trim();
    },
    tooShort() {
      const length = this.displayQuery.length;
      return length > 0 && length < BOOKING_SEARCH_MIN_CHARS;
    },
    filteredBookings() {
      if (this.rangeFilter === "all") return this.bookings;
      const todayStart = new Date();
      todayStart.setUTCHours(0, 0, 0, 0);
      const cutoff = todayStart.getTime();
      if (this.rangeFilter === "upcoming") {
        return this.bookings.filter(row => (row.startMs || 0) >= cutoff);
      }
      return this.bookings.filter(row => (row.startMs || 0) < cutoff);
    },
    emptyCopy() {
      if (this.searching) return "Searching…";
      if (!this.displayQuery) {
        return "Start typing a name, phone number, or email.";
      }
      if (this.tooShort) return "Type at least 2 characters.";
      if (this.bookings.length && !this.filteredBookings.length) {
        return this.rangeFilter === "upcoming"
          ? "Matches found, but none are upcoming. Try All or Past."
          : "Matches found, but none are in the past. Try All or Upcoming.";
      }
      return "Nothing matched. Try fewer letters or a different spelling.";
    }
  },
  watch: {
    shopId: "tryPrefetch",
    searchQuery() {
      this.pagination.currentPage = 1;
      this.scheduleSearch();
    },
    rangeFilter() {
      this.pagination.currentPage = 1;
    }
  },
  created() {
    this.fetchEmployees();
    this.fetchServices();
    this.tryPrefetch();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      fetchServices: "services/fetchServices"
    }),
    highlight(text) {
      return highlightSearchText(text, this.searchQuery);
    },
    matchLine(item) {
      return formatMatchedIn(item.matchedIn);
    },
    serviceLine(item) {
      return [item.serviceName, item.packageName, item.employeeName]
        .filter(Boolean)
        .join(" · ");
    },
    rowClass() {
      return "booking-search__row";
    },
    sortCompare(aRow, bRow, key) {
      if (key === "date" || key === "time") {
        return (aRow.startMs || 0) - (bRow.startMs || 0);
      }
      return false;
    },
    tryPrefetch() {
      if (this.shopId) {
        bookings.prefetchSearchableBookings(this.shopId);
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.runSearch();
      }, 220);
    },
    runSearchNow() {
      clearTimeout(this.searchTimer);
      this.runSearch();
    },
    clearSearch() {
      this.searchQuery = "";
      this.bookings = [];
      this.searched = false;
      this.pagination.currentPage = 1;
    },
    openOnCalendar(row) {
      if (!row || !row.startDate) return;
      this.$router.push(`/bookings/overview/${row.startDate}`);
    },
    async runSearch() {
      const query = this.displayQuery;
      if (!query || query.length < BOOKING_SEARCH_MIN_CHARS) {
        this.bookings = [];
        this.searched = query.length > 0;
        return;
      }
      if (!this.shopId) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: "Shop is not loaded yet. Try again in a moment."
        });
        return;
      }

      const showBusy = !bookings.isSearchCacheReady(this.shopId);
      if (showBusy) this.searching = true;
      try {
        this.bookings = await bookings.searchStoreBookings(query, this.shopId, {
          lookups: {
            employeeById: this.employeeById,
            serviceById: this.serviceById
          }
        });
        this.searched = true;
      } catch (e) {
        this.bookings = [];
        this.searched = true;
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: errorMessage(e, "Search failed. Please try again.")
        });
      } finally {
        this.searching = false;
      }
    }
  }
};
</script>

<style scoped>
.booking-search__bar {
  display: flex;
  align-items: stretch;
}

.booking-search__field {
  position: relative;
  flex: 1 1 260px;
  min-width: 220px;
}

.booking-search__input {
  margin-bottom: 0;
}

.booking-search__input.has-clear >>> .form-control {
  padding-right: 36px;
}

.booking-search__clear {
  position: absolute;
  top: 50%;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #9a9a9a;
  transform: translateY(-50%);
  cursor: pointer;
}

.booking-search__clear:hover,
.booking-search__clear:focus {
  background: #f0f0f0;
  color: #444;
  outline: none;
}

.booking-search__clear i {
  font-size: 12px;
  line-height: 1;
}

.booking-search__hint,
.booking-search__status {
  color: #9a9a9a;
  font-size: 13px;
}

.booking-search__hint {
  margin: 4px 0 12px;
}

.booking-search__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.booking-search__filters {
  display: flex;
  gap: 6px;
}

.booking-search__chip {
  border: 1px solid #e3e3e3;
  background: #fff;
  color: #444;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}

.booking-search__chip.is-on {
  background: #2c2c2c;
  border-color: #2c2c2c;
  color: #fff;
}

.booking-search__pagesize {
  max-width: 90px;
}

.booking-search__name strong {
  display: block;
}

.booking-search__badges {
  margin-top: 4px;
}

.booking-search__badges .badge {
  margin-right: 4px;
}

.booking-search__sub,
.booking-search__match {
  font-size: 12px;
  color: #9a9a9a;
  font-weight: 400;
}

.booking-search__match {
  margin-top: 2px;
}

.booking-search >>> .booking-search__row {
  cursor: pointer;
}

.booking-search >>> mark {
  background: #ffe08a;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}
</style>
